import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { venueApi } from "../../../api/resources";
import { templateApi, type TemplateInput } from "../../../api/templates";
import type { ShiftTemplate, VenueAdmin } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";
import { EmployeePicker } from "../shifts/EmployeePicker";
import { RoleSelect } from "../shifts/RoleSelect";

const sel = "h-11 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";
const blank: TemplateInput = { name: "", venue_id: "", role_id: "", start_time_local: null, end_time_local: null, ends_next_day: false, instructions: null, default_employee_ids: [] };

const toInput = (t: ShiftTemplate): TemplateInput => ({
  name: t.name, venue_id: t.venue_id, role_id: t.role_id, start_time_local: t.start_time_local, end_time_local: t.end_time_local,
  ends_next_day: t.ends_next_day, instructions: t.instructions, default_employee_ids: t.default_employee_ids,
});

export function TemplateFormModal({ open, editing, onClose, onSaved }: { open: boolean; editing: ShiftTemplate | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<TemplateInput>(blank);
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(editing ? toInput(editing) : blank); setError(null); } }, [open, editing]);
  useEffect(() => { venueApi.list(undefined, "ACTIVE").then(setVenues).catch(() => setVenues([])); }, []);
  const set = (p: Partial<TemplateInput>) => setF((x) => ({ ...x, ...p }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await (editing ? templateApi.update(editing.id, f) : templateApi.create(f));
      toast.success(editing ? "Template saved" : "Template created");
      onSaved();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit template" : "New template"} testId="template-form-modal" wide>
      <form onSubmit={submit} className="space-y-5">
        {editing && <p className="text-xs text-zinc-500" data-testid="template-edit-note">Changes apply to future runs only. Shifts already created from this template are not changed.</p>}
        <Field label="Name" htmlFor="tpl-name"><TextInput id="tpl-name" value={f.name} onChange={(e) => set({ name: e.target.value })} required maxLength={80} data-testid="template-name-input" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Venue" htmlFor="tpl-venue">
            <select id="tpl-venue" className={sel} value={f.venue_id} onChange={(e) => set({ venue_id: e.target.value })} required data-testid="template-venue-select">
              <option value="">Select a venue…</option>
              {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Role" htmlFor="shift-role"><RoleSelect value={f.role_id} legacy={null} onChange={(v) => set({ role_id: v })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start (venue time)" htmlFor="tpl-start"><TextInput id="tpl-start" type="time" value={f.start_time_local ?? ""} onChange={(e) => set({ start_time_local: e.target.value || null })} data-testid="template-start-input" /></Field>
          <Field label="End (venue time)" htmlFor="tpl-end"><TextInput id="tpl-end" type="time" value={f.end_time_local ?? ""} onChange={(e) => set({ end_time_local: e.target.value || null })} data-testid="template-end-input" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={f.ends_next_day} onChange={(e) => set({ ends_next_day: e.target.checked })} data-testid="template-overnight-checkbox" /> Ends the next day (overnight)</label>
        <Field label="Instructions (optional)" htmlFor="tpl-instr">
          <textarea id="tpl-instr" rows={3} maxLength={2000} className="w-full rounded-sm border border-white/15 bg-surface p-3 text-sm text-white" value={f.instructions ?? ""} onChange={(e) => set({ instructions: e.target.value || null })} data-testid="template-instructions-input" />
        </Field>
        <div><p className="label-caps mb-2 text-zinc-400">Default crew (optional)</p><EmployeePicker selected={f.default_employee_ids} onChange={(ids) => set({ default_employee_ids: ids })} /></div>
        <ErrorText message={error} testId="template-form-error" />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="template-form-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="template-form-submit">{busy ? "Saving…" : "Save template"}</Button>
        </div>
      </form>
    </Modal>
  );
}
